import { useEffect, useState } from 'react';
import styles from './PatientCaseDoctorBlock.module.scss';
import { AssignDoctorControls } from './components/AssignDoctorControls/AssignDoctorControls';
import { DiagnosisBlock } from './components/DiagnosisBlock/DiagnosisBlock';
import { TreatmentBlock } from './components/TreatmentBlock/TreatmentBlock';
import cx from 'classnames';
import { Icon, RolePermissionChecker, Text } from 'components';
import { selectCurrentUser } from 'store/auth/selectors';
import { getUsers } from 'store/users/selectors';
import { useSelector } from 'store/utils';
import { TreatmentPlanTask, UserRole } from 'types';

type Props = {
  id: number;
  assignDoctorID?: string;
  diagnosis: string;
  treatmentPlan: string;
  task: TreatmentPlanTask;
  isBlocked: boolean;
  isArchived: boolean;
};

const allowToEditTreatmentAndDiagnosisTask = [
  TreatmentPlanTask.LETTER_TO_DO,
  TreatmentPlanTask.LETTER_SUBMITTED,
  TreatmentPlanTask.LETTER_SENT,
  TreatmentPlanTask.DIAGNOSIS_SUBMITTED,
  TreatmentPlanTask.DIAGNOSIS_TO_DO,
];
const allowToEditDiagnosis = [TreatmentPlanTask.DIAGNOSIS_TO_DO];
const allowToRemoveAssignment = [TreatmentPlanTask.DIAGNOSIS_TO_DO];

export const PatientCaseDoctorBlock = ({
  assignDoctorID,
  diagnosis,
  treatmentPlan,
  id,
  task,
  isBlocked,
  isArchived,
}: Props) => {
  const [diagnosisEditMode, setDiagnosisEditMode] = useState(false);
  const [treatmentEditMode, setTreatmentEditMode] = useState(false);

  const currentUser = useSelector(selectCurrentUser);
  const users = useSelector(getUsers);

  const showAssignmentControls = task !== TreatmentPlanTask.TO_SUBMIT && !isArchived && !isBlocked;
  const isDiagnosisAllowedToEdit = allowToEditDiagnosis.includes(task);
  const isTreatmentPlanAllowedToEdit = allowToEditTreatmentAndDiagnosisTask.includes(task);

  const doctorCanRemoveAssignment =
    !isBlocked && allowToRemoveAssignment.includes(task) && !diagnosis && !treatmentPlan;

  const currentUserID = currentUser?.id;
  const doctorName = users.find((doctor) => doctor.id === assignDoctorID)?.name;

  const showDiagnosisControls = !isBlocked && isDiagnosisAllowedToEdit && assignDoctorID === currentUserID;
  const showTreatmentControls = !isBlocked && isTreatmentPlanAllowedToEdit && assignDoctorID === currentUserID;

  const allowNoneAligner = task === TreatmentPlanTask.DIAGNOSIS_TO_DO;

  useEffect(() => {
    setDiagnosisEditMode(false);
    setTreatmentEditMode(false);
  }, [id]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.doctorWrapper}>
        <div className={styles.doctorBlock}>
          <div className={styles.iconWrapper}>
            <Icon name="Tooth" className={styles.plusIcon} />
          </div>
          <div className={styles.doctorInfo}>
            <Text type="caption" className={styles.doctorTitle}>
              Doctor
            </Text>
            {doctorName ? (
              <Text type="caption">{doctorName}</Text>
            ) : (
              <Text type="caption" className={styles.doctorNotAssigned}>
                Not assigned
              </Text>
            )}
          </div>
          {showAssignmentControls && (
            <RolePermissionChecker rolesAllowedToView={[UserRole.DOCTOR]}>
              <AssignDoctorControls
                patientCaseId={id}
                canRemoveAssignment={doctorCanRemoveAssignment}
                assignDoctorID={assignDoctorID}
              />
            </RolePermissionChecker>
          )}
        </div>
      </div>
      <div>
        <div className={styles.blockWrapper}>
          <div className={styles.infoBlock}>
            <Text type="overline" className={styles.upperCaseText}>
              Diagnosis
            </Text>
            {!diagnosisEditMode && (
              <>
                <Text type="caption" className={cx(diagnosis ? '' : styles.textNotSet)}>
                  {diagnosis ? diagnosis : 'Please fill in the diagnosis'}
                </Text>
                {diagnosis && (
                  <div className={styles.diagnosisByDoctorName}>
                    <Icon name="Tooth" className={styles.iconTooth} />
                    <Text type="caption" className={styles.textNotSet}>
                      {doctorName}
                    </Text>
                  </div>
                )}
              </>
            )}
          </div>
          <RolePermissionChecker rolesAllowedToView={[UserRole.DOCTOR]}>
            {!diagnosisEditMode && showDiagnosisControls && (
              <div
                className={cx(styles.iconWrapper, { [styles.iconWhite]: !!diagnosis })}
                onClick={() => setDiagnosisEditMode(true)}>
                {<Icon name={diagnosis ? 'Edit' : 'Plus'} />}
              </div>
            )}
          </RolePermissionChecker>
        </div>
        {diagnosisEditMode && (
          <DiagnosisBlock
            diagnosis={diagnosis}
            treatmentPlan={treatmentPlan}
            id={id}
            onClose={() => setDiagnosisEditMode(false)}
          />
        )}
      </div>
      <div className={styles.line}></div>
      <div className={styles.blockWrapper}>
        <div className={styles.treatmentWrapper}>
          <div className={styles.infoBlock}>
            <Text type="overline" className={styles.upperCaseText}>
              Treatment plan
            </Text>
            {!treatmentEditMode && (
              <Text type="caption" className={treatmentPlan ? '' : styles.textNotSet}>
                {treatmentPlan ? treatmentPlan : 'Please fill in the treatment plan'}
              </Text>
            )}
          </div>
        </div>
        <RolePermissionChecker rolesAllowedToView={[UserRole.DOCTOR]}>
          {!treatmentEditMode && showTreatmentControls && (
            <div
              className={cx(styles.iconWrapper, { [styles.iconWhite]: !!diagnosis })}
              onClick={() => setTreatmentEditMode(true)}>
              {<Icon name={treatmentPlan ? 'Edit' : 'Plus'} />}
            </div>
          )}
        </RolePermissionChecker>
      </div>
      {treatmentEditMode && (
        <TreatmentBlock
          diagnosis={diagnosis}
          treatmentPlan={treatmentPlan}
          id={id}
          onClose={() => setTreatmentEditMode(false)}
          allowNoneAligner={allowNoneAligner}
        />
      )}
    </div>
  );
};
